import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plane, DollarSign, MapPin, Globe, Wifi, Home, TrendingUp, ThermometerSun, Bus, AlertTriangle, Coffee, Smartphone, BookOpen } from 'lucide-react';
import { countries } from '../data/mockData';
import './CountryDetail.css';

function CountryDetail() {
  const { id } = useParams();
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCountry();
  }, [id]);  // Reload when country ID changes

  const loadCountry = useCallback(() => {
    try {
      setLoading(true);
      // Use mockData directly (contains all 22 countries with complete data)
      const mockCountry = countries.find(c => c.id === id);
      setCountry(mockCountry);
    } catch (error) {
      console.error('Error loading country:', error);
      setCountry(null);
    } finally {
      setLoading(false);
    }
  }, [id, countries]);  // Dependencies: id and countries array

  if (loading) {
    return (
      <div className="section">
        <p>Lädt...</p>
      </div>
    );
  }

  if (!country) {
    return (
      <div className="section">
        <p>Land nicht gefunden</p>
        <Link to="/" className="btn-primary">Zurück zur Startseite</Link>
      </div>
    );
  }

  return (
    <div className="country-detail">
      <div className="detail-hero" style={{ backgroundImage: `url(${country.image})` }}>
        <div className="detail-hero-overlay"></div>
        <div className="detail-hero-content">
          <Link to="/" className="back-btn">
            <ArrowLeft size={20} />
            Zurück
          </Link>
          <h1 className="detail-title">{country.name}</h1>
          <p className="detail-continent">{country.continent}</p>
        </div>
      </div>

      <div className="detail-content section">
        {/* Optimal Airports */}
        <div className="detail-section card">
          <h2 className="detail-section-title">
            <Plane size={24} />
            Optimale Flughäfen aus DACH
          </h2>
          <div className="airports-grid">
            {country.optimalAirports.map((airport) => (
              <div key={`airport-${airport.name}-${airport.city}`} className="airport-card">
                <h4>{airport.name}</h4>
                <div className="airport-info">
                  <div className="info-item">
                    <span className="info-label">Direktflüge:</span>
                    <span className="info-value">{airport.directFlights}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Ø Preis:</span>
                    <span className="info-value">{airport.avgPrice}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Parken:</span>
                    <span className="info-value">{airport.parking}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Scores */}
        <div className="detail-section card">
          <h2 className="detail-section-title">
            <TrendingUp size={24} />
            Länder-Scores
          </h2>
          <div className="scores-grid">
            <div className="score-item">
              <div className="score-label">Freundlichkeit</div>
              <div className="score-bar">
                <div className="score-fill" style={{ width: `${country.scores.friendliness * 10}%` }}></div>
              </div>
              <div className="score-value">{country.scores.friendliness}/10</div>
            </div>
            <div className="score-item">
              <div className="score-label">Easy Travel</div>
              <div className="score-bar">
                <div className="score-fill" style={{ width: `${country.scores.easyTravel * 10}%` }}></div>
              </div>
              <div className="score-value">{country.scores.easyTravel}/10</div>
            </div>
            <div className="score-item">
              <div className="score-label">Frauensicherheit</div>
              <div className="score-bar">
                <div className="score-fill" style={{ width: `${country.scores.womenSafety * 10}%` }}></div>
              </div>
              <div className="score-value">{country.scores.womenSafety}/10</div>
            </div>
            <div className="score-item">
              <div className="score-label">Nachhaltigkeit</div>
              <div className="score-bar">
                <div className="score-fill" style={{ width: `${country.scores.sustainability * 10}%` }}></div>
              </div>
              <div className="score-value">{country.scores.sustainability}/10</div>
            </div>
          </div>
        </div>

        {/* Travel Tips - NEW SECTION */}
        {country.travel_tips && (
          <div className="detail-section card travel-tips-section">
            <h2 className="detail-section-title">
              <MapPin size={24} />
              Reise-Tipps & Insider-Wissen
            </h2>

            {/* Highlights & Hidden Gems */}
            {(country.travel_tips.highlights || country.travel_tips.hidden_gems) && (
              <div className="tips-subsection">
                <h3 className="tips-subtitle">🌟 Highlights & Hidden Gems</h3>
                <div className="tips-two-col">
                  {country.travel_tips.highlights && (
                    <div>
                      <h4 className="tips-label">Klassiker:</h4>
                      <ul className="tips-list">
                        {country.travel_tips.highlights.map((item) => (
                          <li key={`highlight-${item.substring(0, 30)}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {country.travel_tips.hidden_gems && (
                    <div>
                      <h4 className="tips-label">Hidden Gems:</h4>
                      <ul className="tips-list">
                        {country.travel_tips.hidden_gems.map((item) => (
                          <li key={`gem-${item.substring(0, 30)}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mobility & Costs */}
            {country.travel_tips.mobility_tips && country.travel_tips.mobility_tips.length > 0 && (
              <div className="tips-subsection">
                <h3 className="tips-subtitle">🚗 Mobilität & Kosten</h3>
                <ul className="tips-list">
                  {country.travel_tips.mobility_tips.map((tip) => (
                    <li key={`mobility-${tip.substring(0, 30)}`}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Eating Tips */}
            {country.travel_tips.eating_tips && country.travel_tips.eating_tips.length > 0 && (
              <div className="tips-subsection">
                <h3 className="tips-subtitle">🍽️ Essen vs. Kochen</h3>
                <ul className="tips-list">
                  {country.travel_tips.eating_tips.map((tip) => (
                    <li key={`eating-${tip.substring(0, 30)}`}>{tip}</li>
                  ))}
                </ul>
                {country.travel_tips.too_good_to_go && (
                  <div className="tips-highlight">
                    <strong>Too Good To Go:</strong> {country.travel_tips.too_good_to_go}
                  </div>
                )}
              </div>
            )}

            {/* Entry & License */}
            {country.travel_tips.entry_requirements && country.travel_tips.entry_requirements.length > 0 && (
              <div className="tips-subsection">
                <h3 className="tips-subtitle">✈️ Einreise & Führerschein</h3>
                <ul className="tips-list">
                  {country.travel_tips.entry_requirements.map((req) => (
                    <li key={`entry-${req.substring(0, 30)}`}>{req}</li>
                  ))}
                  <li>
                    <strong>Internationaler Führerschein:</strong>{' '}
                    {country.travel_tips.international_license_needed ? (
                      <span className="badge-required">Erforderlich ✓</span>
                    ) : (
                      <span className="badge-not-required">Nicht erforderlich</span>
                    )}
                  </li>
                </ul>
              </div>
            )}

            {/* Medication & Protection */}
            {country.travel_tips.medication_tips && country.travel_tips.medication_tips.length > 0 && (
              <div className="tips-subsection">
                <h3 className="tips-subtitle">💊 Medikamente & Schutz</h3>
                <ul className="tips-list">
                  {country.travel_tips.medication_tips.map((tip) => (
                    <li key={`med-${tip.substring(0, 30)}`}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Birthday Freebies */}
            {country.travel_tips.birthday_freebies && country.travel_tips.birthday_freebies.length > 0 && (
              <div className="tips-subsection birthday-section">
                <h3 className="tips-subtitle">🎂 Geburtstags-Gratisangebote</h3>
                <ul className="tips-list">
                  {country.travel_tips.birthday_freebies.map((freebie) => (
                    <li key={`birthday-${freebie.substring(0, 30)}`}>{freebie}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Value Score for Nomads */}
        <div className="detail-section card">
          <h2 className="detail-section-title">
            <Globe size={24} />
            Digital Nomad Score
          </h2>
          <div className="nomad-stats">
            <div className="nomad-stat">
              <Wifi className="nomad-icon" />
              <div>
                <div className="nomad-label">Internet</div>
                <div className="nomad-value">{country.valueScore.internetSpeed}</div>
              </div>
            </div>
            <div className="nomad-stat">
              <DollarSign className="nomad-icon" />
              <div>
                <div className="nomad-label">Lebenshaltung</div>
                <div className="nomad-value">{country.valueScore.livingCosts}</div>
              </div>
            </div>
            <div className="nomad-stat">
              <Home className="nomad-icon" />
              <div>
                <div className="nomad-label">Miete</div>
                <div className="nomad-value">{country.valueScore.rent}</div>
              </div>
            </div>
            <div className="nomad-stat">
              <TrendingUp className="nomad-icon" />
              <div>
                <div className="nomad-label">Gesamt-Score</div>
                <div className="nomad-value">{country.valueScore.nomadScore}/10</div>
              </div>
            </div>
          </div>
        </div>

        {/* Optimal Travel Time */}
        <div className="detail-section card">
          <h2 className="detail-section-title">
            <ThermometerSun size={24} />
            Beste Reisezeit
          </h2>
          <div className="travel-time-info">
            <div className="time-period">{country.optimalTravelTime.best}</div>
            <p className="time-reason">{country.optimalTravelTime.reason}</p>
          </div>

          <h3 className="subsection-title">Wetter-Tabelle</h3>
          <div className="weather-grid">
            {country.weather.map((w, index) => (
              <div key={index} className="weather-month">
                <div className="weather-month-name">{w.month}</div>
                <div className="weather-temp">{w.temp}</div>
                <div className="weather-rain">{w.rain}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Transportation */}
        <div className="detail-section card">
          <h2 className="detail-section-title">
            <Bus size={24} />
            Fortbewegung vor Ort
          </h2>
          <div className="transport-grid">
            {country.transportation.map((t, index) => (
              <div key={index} className="transport-card">
                <h4>{t.type}</h4>
                <div className="transport-details">
                  <span className="transport-cost">{t.cost}</span>
                  <span className="transport-speed">Geschwindigkeit: {t.speed}</span>
                  <span className="transport-comfort">Komfort: {t.comfort}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* No-Go's */}
        <div className="detail-section card alert-section">
          <h2 className="detail-section-title">
            <AlertTriangle size={24} />
            No-Go's & Verhaltensregeln
          </h2>
          <ul className="nogo-list">
            {country.noGos.map((nogo, index) => (
              <li key={index} className="nogo-item">{nogo}</li>
            ))}
          </ul>
        </div>

        {/* Average Costs */}
        <div className="detail-section card">
          <h2 className="detail-section-title">
            <Coffee size={24} />
            Durchschnittskosten
          </h2>
          <div className="costs-grid">
            <div className="cost-item">
              <span className="cost-label">☕ Kaffee</span>
              <span className="cost-value">{country.avgCosts.coffee}</span>
            </div>
            <div className="cost-item">
              <span className="cost-label">🍺 Bier</span>
              <span className="cost-value">{country.avgCosts.beer}</span>
            </div>
            <div className="cost-item">
              <span className="cost-label">🍷 Wein</span>
              <span className="cost-value">{country.avgCosts.wine}</span>
            </div>
            <div className="cost-item">
              <span className="cost-label">💧 Wasser</span>
              <span className="cost-value">{country.avgCosts.water}</span>
            </div>
            <div className="cost-item">
              <span className="cost-label">🍽️ Lokales Essen</span>
              <span className="cost-value">{country.avgCosts.localFood}</span>
            </div>
            <div className="cost-item">
              <span className="cost-label">💆 Massage</span>
              <span className="cost-value">{country.avgCosts.massage}</span>
            </div>
            <div className="cost-item">
              <span className="cost-label">💅 Maniküre</span>
              <span className="cost-value">{country.avgCosts.manicure}</span>
            </div>
            <div className="cost-item">
              <span className="cost-label">💉 Botox</span>
              <span className="cost-value">{country.avgCosts.botox}</span>
            </div>
            <div className="cost-item">
              <span className="cost-label">🦷 Zahnbleaching</span>
              <span className="cost-value">{country.avgCosts.teethWhitening}</span>
            </div>
          </div>
        </div>

        {/* Apps */}
        <div className="detail-section card">
          <h2 className="detail-section-title">
            <Smartphone size={24} />
            Wichtige Apps
          </h2>
          <div className="apps-grid">
            {country.apps.map((app, index) => (
              <div key={index} className="app-card">
                <h4>{app.name}</h4>
                <p className="app-purpose">{app.purpose}</p>
                <a 
                  href={app.affiliate === '#' || !app.affiliate ? `https://www.google.com/search?q=${encodeURIComponent(app.name + ' app')}` : app.affiliate} 
                  className="app-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Zum Download →
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Travel Guides */}
        <div className="detail-section card">
          <h2 className="detail-section-title">
            <BookOpen size={24} />
            Empfohlene Reiseführer
          </h2>
          <div className="guides-list">
            {country.travelGuides.map((guide, index) => (
              <div key={index} className="guide-item">
                <div>
                  <h4>{guide.title}</h4>
                  <span className="guide-price">{guide.price}</span>
                </div>
                <a 
                  href={guide.link} 
                  className="btn-outline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Kaufen
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CountryDetail;
